/**
 * star_field — the night sky's stars as PINPOINTS ON THE SPHERE (2026-09-11; the night-sky realism wave 2026-10-06).
 *
 * Every catalogued star (lib/sky/star_catalog.ts — extracted from the astrophoto by
 * tools/asset-pipeline/extract-sky-stars.mjs) carries a visual MAGNITUDE (its flux rank matched to the real sky's star
 * counts — night_sky_model.catalogMagnitudes) and is drawn by POGSON as a CONTRAST over the sky behind it:
 * c = STAR_C_THR · 10^(0.4·(NELM − m)), faded out at the LOCAL limiting magnitude — so a dark sky shows thousands of
 * faint points under a few brilliant ones, and a city's bright sky drowns all but the brightest (night_sky_model's
 * header has the model and its sources). The magnitude is extincted toward the horizon (EXT_K per airmass, reddened).
 *
 * THE POINT: one pixel, not a blob. A star is a Gaussian of σ STAR_CORE_PX (0.45 px) BOX-INTEGRATED over the pixel
 * (an erf product in the pixel's own screen axes, from the view direction's derivatives), normalised so a centred star
 * reads exactly its contrast and an off-centre one spreads the same light over its neighbours — it never vanishes
 * between pixels (the anti-shimmer law: dim, never shrink). The photo's own PSF width (the upscaler's blur) is no longer
 * the star's size. Only stars brighter than STAR_GLARE_MAG earn a small glare halo; the brightest saturate and bloom.
 * Faint stars are colourless (scotopic vision), the bright keep a subtle hue; every star scintillates by airmass.
 *
 * ONE CELL PER PIXEL: the direction's cube-face cell is fetched from the cell table (start, count) and its records
 * looped (two RGBA32F texels each); the bucketing inserted every star into every cell its reach disc touches, so no
 * neighbourhood walk and nothing pops at a cell edge — as long as the shader draws NOTHING past `reach` (the window
 * term — THE NO-POPPING LAW; the reach formula lives in star_catalog.ts). A cell lists its stars bright-first, so the
 * loop STOPS at the first star fainter than the local cut — under a city's sky it visits a handful.
 *
 * NO-CYCLE RULE: parameters in, node out; this file imports nothing from SkyModule.
 */
import {
  Fn, float, int, vec3, ivec2, abs, floor, min, max, clamp, exp, exp2, length, dot, sqrt,
  smoothstep, select, sin, time, textureLoad, Loop, If, Break, mix,
} from 'three/tsl';
import type { Texture } from 'three/webgpu';
import {
  STAR_C_THR, STAR_FADE_IN, STAR_FADE_OUT, STAR_CORE_PX, STAR_GLARE_MAG, STAR_GLARE_PX, STAR_GLARE_AMP,
  MAG_PACK_OFFSET, MAG_PACK_STEPS, erf as erfCpu,
} from './night_sky_model.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export interface StarFieldInput {
  /** The cell table: 6·G wide × G high RGBA32F, (entry start, count, 0, 0). */
  cells: Texture;
  /** The star records: `recordsWidth` wide RGBA32F, two texels per entry. */
  records: Texture;
  grid: number;
  recordsWidth: number;
  /** Loop cap per cell (the bucketing sorted each cell bright-first). */
  maxPerCell: number;
}

export interface StarFieldUniforms {
  /** Scintillation weight (airmass-scaled; 0 = steady). */
  airW: N;
  /** Master brightness gain (uniform; 1 = the model). */
  gain: N;
  /** The view direction's screen derivatives (dFdx, dFdy) — computed by the CALLER in uniform control flow (the loop
   *  runs inside a per-pixel branch, where a derivative is undefined). */
  dx: N;
  dy: N;
  /** The local sky (lib/sky/night_sky.ts buildNightSkyBase): the display luminance a star's contrast multiplies (units),
   *  the naked-eye limiting magnitude, the extinction in magnitudes and its colour. */
  bgDisp: N;
  nelm: N;
  extMag: N;
  extTint: N;
  /** The brightest a star's pixel may be, in sky units (the emissive cap over the key). */
  capUnits: N;
}

/** Cube-face cell of a unit direction — the TSL mirror of star_catalog's `cubeCellOf`. */
export function cubeCellNode(d: N, G: number): { face: N; i: N; j: N } {
  const ax = abs(d.x), ay = abs(d.y), az = abs(d.z);
  const isX = ax.greaterThanEqual(ay).and(ax.greaterThanEqual(az));
  const isY = isX.not().and(ay.greaterThanEqual(az));
  const ma = select(isX, ax, select(isY, ay, az));
  const s = select(isX, d.y, d.x);
  const t = select(isX.or(isY), d.z, d.y);
  const face = select(isX, select(d.x.greaterThan(0), float(0), float(1)),
    select(isY, select(d.y.greaterThan(0), float(2), float(3)), select(d.z.greaterThan(0), float(4), float(5))));
  const i = clamp(floor(s.div(ma).add(1).mul(0.5 * G)), 0, G - 1);
  const j = clamp(floor(t.div(ma).add(1).mul(0.5 * G)), 0, G - 1);
  return { face, i, j };
}

/** Unpack star_catalog's `packColor` (r·65536 + g·256 + b, each ×100). */
const unpackColor = (p: N): N => {
  const r = floor(p.div(65536));
  const rest = p.sub(r.mul(65536));
  const g = floor(rest.div(256));
  const b = rest.sub(g.mul(256));
  return vec3(r, g, b).mul(0.01);
};

/** erf (Abramowitz–Stegun 7.1.26) — night_sky_model.erf's twin. */
const erfNode = (x: N): N => {
  const a = abs(x);
  const t = float(1).div(a.mul(0.3275911).add(1));
  const poly = t.mul(1.061405429).sub(1.453152027).mul(t).add(1.421413741).mul(t).sub(0.284496736).mul(t).add(0.254829592).mul(t);
  const y = float(1).sub(poly.mul(exp(a.mul(a).negate())));
  return select(x.lessThan(0), y.negate(), y);
};

/**
 * The star field's radiance along `viewDir` (unit, world frame), in SKY UNITS (the caller multiplies by `unitScene`),
 * extincted and reddened already.
 */
export function buildStarField(viewDir: N, inp: StarFieldInput, u: StarFieldUniforms): N {
  const G = inp.grid, RW = inp.recordsWidth;
  const K = 1 / (Math.SQRT2 * STAR_CORE_PX);
  const centre = 2 * erfCpu(0.5 * K); // the box share of a centred star, per axis (×2: erf(+) − erf(−))
  return Fn(() => {
    const d = viewDir;
    const { face, i, j } = cubeCellNode(d, G);
    const cell = textureLoad(inp.cells, ivec2(face.mul(G).add(i), j));
    const start = cell.x, count = cell.y;
    const n = min(count, float(inp.maxPerCell)).toInt();
    // The pixel's own screen axes on the sphere (chord per pixel), for the box integral and the glare radius.
    const dxx = max(dot(u.dx, u.dx), 1e-14), dyy = max(dot(u.dy, u.dy), 1e-14);
    const pxSize = sqrt(max(dxx, dyy));
    // The faintest RAW magnitude worth a visit here (the cut is extincted: m + extMag ≤ NELM + fade-out).
    const mCut = u.nelm.add(STAR_FADE_OUT).sub(u.extMag);
    const acc = vec3(0).toVar();
    Loop({ start: int(0), end: n, type: 'int', condition: '<' }, ({ i: k }: { i: N }) => {
      const e = start.toInt().add(k).mul(2).toFloat(); // first texel of the entry
      const ty = floor(e.div(RW));
      const tx = e.sub(ty.mul(RW));
      const t1 = textureLoad(inp.records, ivec2(tx.add(1), ty));
      // record[7] = round((m + 4)·64) + phase·0.999 (night_sky_model.packMagPhase)
      const q = floor(t1.w);
      const m = q.div(MAG_PACK_STEPS).sub(MAG_PACK_OFFSET);
      // Bright-first: every later star in this cell is fainter — stop (the cost of a city sky is a handful of stars).
      If(m.greaterThan(mCut), () => { Break(); });
      const t0 = textureLoad(inp.records, ivec2(tx, ty));
      const sdir = t0.xyz;
      const packed = t1.x, reach = t1.z;
      const phase = t1.w.sub(q).div(0.999);
      const o = sdir.sub(d);
      const dist = length(o); // chord ≈ angle at these radii
      // Pogson over the threshold at the local NELM, faded over the last magnitude (night_sky_model.starContrast).
      const mE = m.add(u.extMag);
      const fade = float(1).sub(smoothstep(u.nelm.sub(STAR_FADE_IN), u.nelm.add(STAR_FADE_OUT), mE));
      const contrast = exp2(u.nelm.sub(mE).mul(0.4 * 3.321928)).mul(STAR_C_THR).mul(fade);
      const peak = min(u.bgDisp.mul(contrast).mul(u.gain), u.capUnits);
      // The box-integrated core in pixel units (night_sky_model.coreShare).
      const px = dot(o, u.dx).div(dxx), py = dot(o, u.dy).div(dyy);
      const ix = erfNode(px.add(0.5).mul(K)).sub(erfNode(px.sub(0.5).mul(K)));
      const iy = erfNode(py.add(0.5).mul(K)).sub(erfNode(py.sub(0.5).mul(K)));
      const core = ix.mul(iy).div(centre * centre);
      // Glare on the brightest only (the eye's and the lens's scatter).
      const glare = exp(dist.div(pxSize.mul(STAR_GLARE_PX)).negate()).mul(STAR_GLARE_AMP)
        .mul(float(1).sub(smoothstep(STAR_GLARE_MAG - 2, STAR_GLARE_MAG, mE))); // (edges ascending — a reversed smoothstep is undefined in WGSL)
      // Draw nothing past the reach the bucketing used (fade over the last 30 %).
      const win = float(1).sub(smoothstep(reach.mul(0.7), reach, dist));
      // Scintillation: two incommensurate sines, per-star phase, airmass-weighted (dim, never shrink).
      const tw = float(1).add(
        sin(time.mul(float(2.2).add(phase.mul(5.0))).add(phase.mul(40))).mul(0.22)
          .add(sin(time.mul(float(6.1).add(phase.mul(9.0))).add(phase.mul(17))).mul(0.12))
          .mul(u.airW),
      );
      // Colour: scotopic for the faint, a subtle hue for the bright (night_sky_model.starSaturation).
      const sat = float(1).sub(smoothstep(-0.5, 2.5, mE)).mul(0.68).add(0.12);
      const col = mix(vec3(1), unpackColor(packed), sat).mul(u.extTint);
      acc.addAssign(col.mul(peak.mul(core.add(glare)).mul(win).mul(tw)));
    });
    return acc;
  })();
}

